import HttpService from "./httpService";

export interface InstagramAuthStatusResponse {
    connected: boolean;
    username?: string;
    expiresAt?: Date | string;
    scopes?: string[];
}

// The account-wide default rule for the comment-to-DM automation. Runs on every
// post/reel; per-post rules override it field-by-field.
export interface AutoDmSettings {
    enabled: boolean;
    replyPublicly: boolean;
    defaultKeywords: string[];
    defaultDmMessage: string;
    defaultCommentReply: string;
}

export interface AutoDmMapping {
    id: string;
    contentType: "REEL" | "POST";
    postIdOrUrl: string;
    keywords: string[];
    dmMessage?: string;
    commentReply?: string;
    status: "ACTIVE" | "PAUSED";
    createdAt?: Date | string;
}

export interface CreateAutoDmMappingInput {
    postIdOrUrl: string;
    keywords?: string[];
    dmMessage?: string;
    commentReply?: string;
}

interface BackendAutoDmMapping {
    _id: string;
    postId?: string;
    postUrl: string;
    mediaType?: "REEL" | "POST";
    keywords?: string[];
    dmMessage?: string;
    commentReply?: string;
    enabled?: boolean;
    createdAt?: Date | string;
    updatedAt?: Date | string;
}

export interface UpdateAutoDmMappingInput {
    postIdOrUrl?: string;
    keywords?: string[];
    dmMessage?: string;
    commentReply?: string;
    enabled?: boolean;
}

interface CreateMappingResponse {
    message: string;
    mapping: BackendAutoDmMapping;
}

interface GetMappingsResponse {
    mappings: BackendAutoDmMapping[];
}

const inferContentType = (postUrl: string): "REEL" | "POST" =>
    postUrl.includes("/reel/") ? "REEL" : "POST";

const normalizeMapping = (mapping: BackendAutoDmMapping): AutoDmMapping => ({
    id: mapping._id,
    contentType: mapping.mediaType || inferContentType(mapping.postUrl),
    postIdOrUrl: mapping.postUrl,
    keywords: mapping.keywords || [],
    dmMessage: mapping.dmMessage,
    commentReply: mapping.commentReply,
    status: mapping.enabled === false ? "PAUSED" : "ACTIVE",
    createdAt: mapping.createdAt,
});

export class AutoDmService {
    static getInstagramAuthUrl = async (): Promise<{ url: string }> =>
        HttpService.get<{ url: string }>("/auto-dm/auth-url");

    static handleInstagramCallback = async ({ code }: { code: string }) =>
        HttpService.post<any>("/auto-dm/callback", { code });

    static getInstagramAuthStatus = async (): Promise<InstagramAuthStatusResponse> =>
        HttpService.get<InstagramAuthStatusResponse>("/auto-dm/status");

    static disconnectInstagram = async (): Promise<{ disconnected: boolean }> =>
        HttpService.delete<{ disconnected: boolean }>("/auto-dm/disconnect");

    // --- Default automation settings ---------------------------------------

    static getSettings = async (): Promise<AutoDmSettings> => {
        const res = await HttpService.get<{ settings: AutoDmSettings }>("/auto-dm/settings");
        return res.settings;
    };

    static updateSettings = async (settings: AutoDmSettings): Promise<AutoDmSettings> => {
        const res = await HttpService.put<{ settings: AutoDmSettings; message: string }>("/auto-dm/settings", settings);
        return res.settings;
    };

    // --- Per-reel/post rules ------------------------------------------------

    static createMapping = async (
        data: CreateAutoDmMappingInput
    ): Promise<{ message: string; mapping: AutoDmMapping }> => {
        const response = await HttpService.post<CreateMappingResponse>("/auto-dm/mappings", {
            postUrl: data.postIdOrUrl,
            keywords: data.keywords || [],
            dmMessage: data.dmMessage,
            commentReply: data.commentReply,
        });
        return { message: response.message, mapping: normalizeMapping(response.mapping) };
    };

    static getMappings = async (): Promise<AutoDmMapping[]> => {
        const response = await HttpService.get<GetMappingsResponse>("/auto-dm/mappings");
        return response.mappings.map(normalizeMapping);
    };

    // Only the keys present are changed server-side. Send "" for dmMessage /
    // commentReply to clear an override and fall back to the default rule.
    static updateMapping = async (
        id: string,
        data: UpdateAutoDmMappingInput
    ): Promise<{ message: string; mapping: AutoDmMapping }> => {
        const payload: Record<string, unknown> = {};
        if (data.postIdOrUrl !== undefined) payload.postUrl = data.postIdOrUrl;
        if (data.keywords !== undefined) payload.keywords = data.keywords;
        if (data.dmMessage !== undefined) payload.dmMessage = data.dmMessage;
        if (data.commentReply !== undefined) payload.commentReply = data.commentReply;
        if (data.enabled !== undefined) payload.enabled = data.enabled;

        const response = await HttpService.put<CreateMappingResponse>(`/auto-dm/mappings/${id}`, payload);
        return { message: response.message, mapping: normalizeMapping(response.mapping) };
    };

    static deleteMapping = async (id: string): Promise<{ message: string }> =>
        HttpService.delete<{ message: string }>(`/auto-dm/mappings/${id}`);
}

export default AutoDmService;
